import React, { useEffect, useMemo, useState } from 'react';

// A mechanical "departure board" readout — the site's signature element.
// Solari split-flap boards are themselves a piece of engineering (Berlin's own
// Hauptbahnhof used them for decades), which is why this, rather than a fading
// carousel, is what cycles the hero's "translation" word through other
// languages: it reads as precise/technical first, decorative second.
//
// Purely presentational: `words` should already be final display strings
// (translated/cased by the caller). Renders one flap cell per character,
// center-padded to the longest word so the board's width does not jump.
export default function SplitFlap({ words, interval = 2600, size = 'lg', className = '' }) {
  const list = useMemo(() => (words || []).filter(Boolean), [words]);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (list.length < 2) return undefined;
    // Cycling the word is motion in itself — hold the first word for
    // reduced-motion visitors (the flip keyframe is already zeroed in CSS).
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setInterval(() => setI((n) => (n + 1) % list.length), interval);
    return () => clearInterval(id);
  }, [list, interval]);

  if (list.length === 0) return null;

  const maxLen = Math.max(...list.map((w) => w.length));
  const word = (list[i] || '').toUpperCase();
  const padStart = Math.ceil((maxLen - word.length) / 2);
  const padded = (' '.repeat(padStart) + word).padEnd(maxLen, ' ');

  return (
    <div
      className={`tt-flap tt-flap--${size} ${className}`}
      role="img"
      aria-label={list.join(' / ')}
    >
      {padded.split('').map((ch, idx) => (
        <span key={`${i}-${idx}`} className="tt-flap__cell" style={{ '--tt-flap-delay': `${idx * 16}ms` }}>
          <span className="tt-flap__char">{ch === ' ' ? '\u00A0' : ch}</span>
        </span>
      ))}
    </div>
  );
}
